import React from 'react';
import { Sparkline } from './charts/Sparkline';
import { useWidth } from './charts/useWidth';
import type { DeltaTone } from './insights';

/**
 * Η λωρίδα με τα νούμερα (5 στην Αρχική, 4 στα Προϊόντα): ετικέτα, νούμερο, Δ (πράσινο/κόκκινο μόνο),
 * τάση 7 ημερών. Χωρίς κουτιά — λεπτές γραμμές ανάμεσα, όπως το φύλλο.
 */
export interface KpiDelta {
  /** «↑ 4,2%» */
  text: string;
  tone: DeltaTone;
  /** «από χθες» */
  suffix: string;
  title?: string;
}

export interface KpiItem {
  id: string;
  label: string;
  value: string;
  unit?: string;
  /** Κείμενο αντί για νούμερο («Ξεχάστηκε», «Καφέδες»). */
  word?: boolean;
  hero?: boolean;
  /** Κόκκινο νούμερο (ξεχασμένο Ζ). */
  bad?: boolean;
  delta?: KpiDelta | null;
  rows?: React.ReactNode[];
  spark?: number[];
  sparkLabel?: string;
}

const deltaClass: Record<DeltaTone, string> = { ok: 'ok', bad: 'bad', flat: 't3', none: 't3' };

/** Μεγάλα ποσά σε μικρότερο μέγεθος — ποτέ «…» σε νούμερο. */
const sizeClass = (k: KpiItem): string => {
  if (k.word) return '';
  const len = k.value.length;
  if (k.hero) return len > 11 ? ' sz-s' : len > 8 ? ' sz-m' : '';
  return len > 9 ? ' sz-s' : len > 7 ? ' sz-m' : '';
};

const KpiCell: React.FC<{ k: KpiItem }> = ({ k }) => {
  const [sparkRef, width] = useWidth(150);
  return (
    <div className={`xh-kpi${k.hero ? ' hero' : ''}${k.bad ? ' is-bad' : ''}`} data-testid={`kpi-${k.id}`}>
      <div className="xh-k-l">{k.label}</div>
      <div className={`xh-k-v${k.word ? ' word' : ''}${sizeClass(k)}`} data-testid={`kpi-${k.id}-value`}>
        {k.value}
        {k.unit && <span className="u">{k.unit}</span>}
      </div>
      {k.delta && (
        <div className="xh-k-d" title={k.delta.title} data-testid={`kpi-${k.id}-delta`}>
          <b className={deltaClass[k.delta.tone]} data-tone={k.delta.tone} data-testid={`kpi-${k.id}-tone`}>{k.delta.text}</b> {k.delta.suffix}
        </div>
      )}
      {k.rows && k.rows.length > 0 && (
        <div className="xh-k-rows">
          {k.rows.map((r, i) => <span key={i}>{r}</span>)}
        </div>
      )}
      {k.spark && k.spark.length > 1 && (
        <div className="xh-k-s" ref={sparkRef}>
          <Sparkline values={k.spark} width={Math.max(40, Math.min(width, 150))} height={26} label={k.sparkLabel || k.label} />
        </div>
      )}
    </div>
  );
};

export const KpiStrip: React.FC<{ items: KpiItem[]; busy?: boolean; ariaLabel?: string }> = ({ items, busy = false, ariaLabel }) => (
  <div className="xh-kpis" data-count={items.length} aria-busy={busy || undefined} aria-label={ariaLabel} role="group" data-testid="kpi-strip">
    {items.map(k => <KpiCell key={k.id} k={k} />)}
  </div>
);

export default KpiStrip;
